import { createContext, useContext } from 'react';
import { useAllABTests } from './ab-test.hook';
import type { TABTestContext, TABTestProviderConfig, TABTestState } from './ab-test.types';

const ABTestContext = createContext<TABTestContext | undefined>(undefined);

/**
 * Provides A/B test state to the component tree.
 * Wraps the app (inside AuthProvider, outside Router) so all
 * components can access test variations via useABTest / useABTestFor.
 *
 * Tests are configured via VITE_AB_TEST_CONFIG env var and resolved
 * through Adobe Target on mount. Results are cached in localStorage.
 */
const ABTestProvider = ({ children }: TABTestProviderConfig) => {
	const tests = useAllABTests();

	return <ABTestContext.Provider value={{ tests }}>{children}</ABTestContext.Provider>;
};

ABTestProvider.displayName = 'ABTestProvider';

/**
 * Returns the full A/B test context (all tests).
 * Must be used within ABTestProvider.
 */
const useABTest = (): TABTestContext => {
	const context = useContext(ABTestContext);

	if (context === undefined) {
		throw new Error('useABTest must be used within an ABTestProvider');
	}

	return context;
};

/**
 * Returns the state of a specific A/B test by key.
 * If the key doesn't exist, returns a safe default (variation: null, not loading, not fetched).
 *
 * @param key - The test name as defined in VITE_AB_TEST_CONFIG (e.g. 'garagemMeusAnuncios')
 */
const useABTestFor = (key: string): TABTestState => {
	const { tests } = useABTest();
	return tests[key] ?? { variation: null, isLoading: false, fetched: false };
};

export { ABTestProvider, useABTest, useABTestFor };
